import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { locationsAPI, itemsAPI, shipmentsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatTime } from '@/lib/utils';
import { 
  Send, 
  Trash2, 
  Store,
  Package,
  Filter,
  Clock,
  CheckCircle,
  X,
  Search,
  Plus,
  Minus,
  Printer
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import PrintTicket from '@/components/PrintTicket';
import { ExportJustoButton } from '@/components/ExportJustoButton';

const ShipmentSection = () => {
  const { warehouseId } = useAuth();
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [todayShipments, setTodayShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Formulario de envío
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [shipmentQuantities, setShipmentQuantities] = useState({}); // {itemId: quantity}
  const [notes, setNotes] = useState('');
  
  // Estados para impresión
  const [printShipmentData, setPrintShipmentData] = useState(null);
  const [successDialog, setSuccessDialog] = useState({ open: false, shipment: null });

  // Cargar datos iniciales
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [locationsRes, itemsRes, shipmentsRes] = await Promise.all([
        locationsAPI.getAll(true),
        itemsAPI.getAll(true),
        shipmentsAPI.getToday()
      ]);
      
      // Filtrar locales por la bodega del usuario
      const filteredLocations = locationsRes.data.filter(
        loc => !warehouseId || loc.warehouse_id === warehouseId
      );
      
      setLocations(filteredLocations);
      setItems(itemsRes.data);
      setTodayShipments(shipmentsRes.data.shipments || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [warehouseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Extraer proveedores únicos
  const suppliers = useMemo(() => {
    const uniqueSuppliers = [...new Set(items.map(item => item.supplier).filter(Boolean))];
    return uniqueSuppliers.sort();
  }, [items]);

  // Obtener stock disponible de un item
  const getAvailableStock = useCallback((item) => {
    const stockByWarehouse = item.stock_by_warehouse || {};
    return warehouseId ? (stockByWarehouse[warehouseId] || 0) : item.current_stock;
  }, [warehouseId]);

  // Filtrar items disponibles (con stock > 0)
  const filteredItems = useMemo(() => {
    let filtered = items.filter(item => {
      const stock = getAvailableStock(item);
      if (stock <= 0) return false;
      
      // Filtrar por proveedor
      if (selectedSupplier !== 'all' && item.supplier !== selectedSupplier) {
        return false;
      }
      
      // Filtrar por búsqueda
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return item.name.toLowerCase().includes(search) || 
               item.code.toLowerCase().includes(search);
      }
      
      return true;
    });
    
    return filtered;
  }, [items, selectedSupplier, searchTerm, getAvailableStock]);

  // Actualizar cantidad de un item
  const updateQuantity = (itemId, value) => {
    const item = items.find(i => i.id === itemId);
    const maxStock = item ? getAvailableStock(item) : 0;
    const newValue = Math.min(Math.max(0, value), maxStock);
    
    setShipmentQuantities(prev => {
      if (newValue === 0) {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: newValue };
    });
  };

  // Incrementar cantidad
  const incrementQuantity = (itemId) => {
    const current = shipmentQuantities[itemId] || 0;
    updateQuantity(itemId, current + 1);
  };

  // Decrementar cantidad
  const decrementQuantity = (itemId) => {
    const current = shipmentQuantities[itemId] || 0;
    updateQuantity(itemId, current - 1);
  };

  // Items con cantidad > 0
  const itemsToShip = useMemo(() => {
    return Object.entries(shipmentQuantities)
      .filter(([_, qty]) => qty > 0)
      .map(([itemId, quantity]) => {
        const item = items.find(i => i.id === itemId);
        return item ? { ...item, quantity } : null;
      })
      .filter(Boolean);
  }, [shipmentQuantities, items]);

  // Calcular totales
  const totalItems = itemsToShip.length;
  const totalQuantity = itemsToShip.reduce((sum, item) => sum + item.quantity, 0);

  // Enviar
  const handleSendShipment = async () => {
    if (!selectedLocation) {
      toast.error('Selecciona un local destino');
      return;
    }
    
    if (itemsToShip.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }
    
    const location = locations.find(l => l.id === selectedLocation);
    
    setSending(true);
    try {
      const response = await shipmentsAPI.create({
        location_id: selectedLocation,
        location_name: location?.name || 'Local',
        items: itemsToShip.map(item => ({
          item_id: item.id,
          item_name: item.name,
          item_code: item.code,
          quantity: item.quantity,
          unit: item.unit,
          supplier: item.supplier
        })),
        notes: notes || null
      });
      
      // Mostrar dialog de éxito con opción de imprimir
      setSuccessDialog({
        open: true,
        shipment: response.data
      });
      
      // Limpiar formulario
      setSelectedLocation('');
      setShipmentQuantities({});
      setNotes('');
      
      // Recargar datos
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear envío');
    } finally {
      setSending(false);
    }
  };

  // Preparar datos para impresión de despacho
  const preparePrintData = (shipment) => {
    if (!shipment || !shipment.items || shipment.items.length === 0) {
      toast.error('Este despacho no contiene productos para imprimir.');
      return null;
    }

    return {
      type: 'shipment',
      warehouseName: shipment.warehouse_name || 'Bodega Central',
      locationName: shipment.location_name,
      orderType: null,
      createdAt: shipment.created_at,
      createdBy: shipment.created_by_name || 'Usuario',
      documentId: shipment.id,
      items: shipment.items.map(item => ({
        item_name: item.item_name,
        quantity: item.quantity, // Cantidad REALMENTE despachada
        unit: item.unit,
        supplier: item.supplier
      }))
    };
  };

  // Imprimir despacho desde dialog de éxito
  const handlePrintFromSuccess = () => {
    const printData = preparePrintData(successDialog.shipment);
    if (printData) {
      setPrintShipmentData(printData);
    }
  };

  // Reimprimir un despacho histórico
  const handleReprint = (shipment) => {
    const printData = preparePrintData(shipment);
    if (printData) {
      setPrintShipmentData(printData);
    }
  };

  // Cerrar modales
  const closePrintModal = () => {
    setPrintShipmentData(null);
  };

  const closeSuccessDialog = () => {
    setSuccessDialog({ open: false, shipment: null });
  };

  // Cancelar envío
  const cancelShipment = async (shipmentId) => {
    if (!window.confirm('¿Cancelar este envío? El stock será devuelto.')) return;
    
    try {
      await shipmentsAPI.cancel(shipmentId);
      toast.success('Envío cancelado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  // Limpiar selección
  const clearSelection = () => {
    setShipmentQuantities({});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shipment-section">
      {/* Header del formulario */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            Nuevo Envío a Local
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            El stock se descontará automáticamente al confirmar
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Selector de Local y Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">
                Local Destino *
              </Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger data-testid="location-select">
                  <SelectValue placeholder="Seleccionar local..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-muted-foreground" />
                        {location.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-1.5 block">
                Filtrar por Proveedor
              </Label>
              <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Todos los proveedores
                    </div>
                  </SelectItem>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier} value={supplier}>
                      {supplier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">
                Buscar Producto
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Nombre o código..."
                  className="pl-10"
                  data-testid="product-search"
                />
              </div>
            </div>
          </div>

          {/* Resumen de selección */}
          {totalItems > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">
                  {totalItems} productos seleccionados • {totalQuantity} unidades en total
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearSelection}
                className="text-green-700 hover:text-green-900 hover:bg-green-100"
              >
                <X className="w-4 h-4 mr-1" />
                Limpiar
              </Button>
            </div>
          )}
          
          {/* Grid de productos (estilo inventario) */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">
                Productos Disponibles ({filteredItems.length})
              </Label>
              {selectedSupplier !== 'all' && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  Filtrado por: {selectedSupplier}
                </span>
              )}
            </div>
            
            {filteredItems.length === 0 ? (
              <div className="bg-slate-50 rounded-lg p-8 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {searchTerm || selectedSupplier !== 'all' 
                    ? 'No se encontraron productos con los filtros aplicados'
                    : 'No hay productos con stock disponible'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto p-1">
                {filteredItems.map((item) => {
                  const stock = getAvailableStock(item);
                  const quantity = shipmentQuantities[item.id] || 0;
                  const isSelected = quantity > 0;
                  
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className={`bg-white rounded-lg border-2 p-4 transition-all ${
                        isSelected 
                          ? 'border-primary bg-primary/5 shadow-md' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {/* Info del producto */}
                      <div className="mb-3">
                        <p className="font-medium text-sm text-slate-900 truncate" title={item.name}>
                          {item.name}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-muted-foreground font-mono">
                            {item.code}
                          </span>
                          <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                            {item.unit}
                          </span>
                        </div>
                        {item.supplier && (
                          <span className="text-xs text-blue-600 mt-1 block truncate">
                            {item.supplier}
                          </span>
                        )}
                      </div>
                      
                      {/* Stock disponible */}
                      <div className="text-center mb-3">
                        <span className="text-xs text-muted-foreground">Stock:</span>
                        <span className="text-lg font-bold text-green-600 ml-1">{stock}</span>
                      </div>
                      
                      {/* Controles de cantidad */}
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => decrementQuantity(item.id)}
                          disabled={quantity <= 0}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        
                        <Input
                          type="number"
                          value={quantity || ''}
                          onChange={(e) => updateQuantity(item.id, parseFloat(e.target.value) || 0)}
                          min={0}
                          max={stock}
                          className="w-16 h-8 text-center font-bold"
                          placeholder="0"
                          data-testid={`qty-${item.id}`}
                        />
                        
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => incrementQuantity(item.id)}
                          disabled={quantity >= stock}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Notas */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">
              Notas / Observaciones (opcional)
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Agregar observaciones sobre el envío..."
              rows={2}
            />
          </div>
          
          {/* Botón de envío */}
          <Button
            onClick={handleSendShipment}
            disabled={sending || !selectedLocation || totalItems === 0}
            className="w-full"
            size="lg"
            data-testid="send-shipment-btn"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Confirmar Envío ({totalItems} productos, {totalQuantity} unidades)
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Envíos del día */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-500" />
            Envíos de Hoy
          </h2>
          <span className="text-sm text-muted-foreground">
            {todayShipments.filter(s => s.status !== 'cancelled').length} envío(s)
          </span>
        </div>
        
        {todayShipments.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-muted-foreground">No hay envíos registrados hoy</p>
          </div>
        ) : (
          <div className="divide-y">
            {todayShipments.map((shipment) => (
              <div key={shipment.id} className={`p-4 hover:bg-slate-50 ${
                shipment.status === 'cancelled' ? 'opacity-60' : ''
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      shipment.status === 'cancelled' ? 'bg-red-100' : 'bg-green-100'
                    }`}>
                      {shipment.status === 'cancelled' ? (
                        <X className="w-5 h-5 text-red-600" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{shipment.location_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatTime(shipment.created_at)}
                        {' • '}
                        {shipment.total_items} producto(s) • {shipment.total_quantity} unidades
                      </p>
                      {shipment.notes && (
                        <p className="text-xs text-slate-500 mt-1 italic">"{shipment.notes}"</p>
                      )}
                    </div>
                  </div>
                  
                  {shipment.status !== 'cancelled' && (
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <ExportJustoButton tipo="shipment" id={shipment.id} />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReprint(shipment)}
                        className="text-slate-600 hover:text-slate-900"
                        data-testid={`reprint-${shipment.id}`}
                      >
                        <Printer className="w-4 h-4 mr-1" />
                        Reimprimir
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelShipment(shipment.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Cancelar
                      </Button>
                    </div>
                  )}
                  
                  {shipment.status === 'cancelled' && (
                    <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                      Cancelado
                    </span>
                  )}
                </div>
                
                {/* Lista de items */}
                <div className="mt-3 ml-13 pl-10">
                  <div className="flex flex-wrap gap-2">
                    {shipment.items.slice(0, 5).map((item, idx) => (
                      <span key={idx} className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {item.item_name}: {item.quantity} {item.unit}
                      </span>
                    ))}
                    {shipment.items.length > 5 && (
                      <span className="text-xs text-muted-foreground">
                        +{shipment.items.length - 5} más
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog de éxito con opción de imprimir */}
      <Dialog open={successDialog.open} onOpenChange={(open) => !open && closeSuccessDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-6 h-6" />
              Despacho registrado correctamente
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              El envío a <strong>{successDialog.shipment?.location_name}</strong> ha sido registrado.
            </p>
            <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
              <p><strong>Productos:</strong> {successDialog.shipment?.total_items}</p>
              <p><strong>Unidades totales:</strong> {successDialog.shipment?.total_quantity}</p>
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={closeSuccessDialog}
            >
              Cerrar
            </Button>
            {successDialog.shipment?.id && <ExportJustoButton tipo="shipment" id={successDialog.shipment.id} size="default" />}
            <Button
              onClick={handlePrintFromSuccess}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="print-shipment-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir despacho
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de impresión de despacho */}
      {printShipmentData && (
        <PrintTicket
          type={printShipmentData.type}
          warehouseName={printShipmentData.warehouseName}
          locationName={printShipmentData.locationName}
          orderType={printShipmentData.orderType}
          createdAt={printShipmentData.createdAt}
          createdBy={printShipmentData.createdBy}
          documentId={printShipmentData.documentId}
          items={printShipmentData.items}
          onClose={closePrintModal}
        />
      )}
    </div>
  );
};

export default ShipmentSection;
